import React from "react";
import clsx from "clsx";

// LQC-2254 - one label/value row of the pivoted ticket. The app has no shared
// label-value primitive (SettingsHTML and TradeWideRow are three unexported
// near-duplicates), so this is the ticket's own, kept exported for the day one is
// lifted app-wide.
interface ITicketRow {
  label: string;
  testId: string;
  children: React.ReactNode;
  // Set while the user hovers a blocked Send button and this row is one of the reasons.
  isHighlighted?: boolean;
  // A reason THIS row is unsendable, shown immediately underneath it. Unlike
  // isHighlighted it does not wait for a Send hover: a date in the wrong order is
  // plainly wrong now, and the footer is off-screen on a long ticket (LQC-2677).
  error?: string;
  // Static per row - the field must be filled before the ticket can be sent.
  isRequired?: boolean;
}

// The "*" against a required row, so the user knows what is needed before hovering a
// blocked Send. Red only once the row is actually flagged.
export const RequiredMarker = ({
  isRequired,
  isHighlighted,
}: {
  isRequired: boolean;
  isHighlighted: boolean;
}) => {
  if (!isRequired) return null;
  return (
    <span
      className={clsx(
        "ml-0.5",
        isHighlighted ? "text-red-600 dark:text-red-400" : "text-gray-400 dark:text-gray-500",
      )}
      title="Required"
    >
      *
    </span>
  );
};

const TicketRow = ({
  label,
  testId,
  children,
  isHighlighted = false,
  error = "",
  isRequired = false,
}: ITicketRow) => {
  const isFlagged = isHighlighted || !!error;
  return (
    <div
      data-testid={testId}
      id={testId}
      className={clsx(
        "grid grid-cols-[minmax(88px,140px)_1fr] items-center gap-x-2 border-b py-1 transition-colors",
        isFlagged
          ? "border-red-500/60 bg-red-500/10"
          : "border-gray-200 dark:border-gray-700",
      )}
    >
      <div
        className={clsx(
          "px-1 text-xs transition-colors",
          isFlagged
            ? "font-semibold text-red-600 dark:text-red-400"
            : "text-gray-600 dark:text-gray-300",
        )}
      >
        {label}
        <RequiredMarker isRequired={isRequired} isHighlighted={isFlagged} />
      </div>
      <div className="min-w-0">{children}</div>
      {error && (
        <div
          data-testid={`${testId}Error`}
          id={`${testId}Error`}
          className="col-start-2 px-1 pb-0.5 text-xs font-semibold text-red-600 dark:text-red-400"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default TicketRow;
